import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { isDemoMode } from "../demo/demoMode";

// You'll need to set these environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
// Prefer the standard anon key name, but fall back to the previous env var if present
const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_DEFAULT_KEY;

if (!isDemoMode() && (!supabaseUrl || !supabaseAnonKey)) {
  throw new Error("Missing Supabase environment variables");
}

// Dev-only sanity checks (no secrets printed)
const isDev = !process.env.NODE_ENV || process.env.NODE_ENV === "development";
if (isDev) {
  if (
    !process.env.REACT_APP_SUPABASE_ANON_KEY &&
    process.env.REACT_APP_SUPABASE_PUBLISHABLE_DEFAULT_KEY
  ) {
    console.warn(
      "Using legacy env REACT_APP_SUPABASE_PUBLISHABLE_DEFAULT_KEY. Rename to REACT_APP_SUPABASE_ANON_KEY."
    );
  }
  if (supabaseUrl && supabaseUrl.endsWith("/")) {
    console.warn(
      "Supabase URL ends with a trailing slash; remove it if possible."
    );
  }
  const looksJwt = !!supabaseAnonKey && supabaseAnonKey.includes(".");
  if (supabaseAnonKey && !looksJwt) {
    console.warn(
      "Supabase anon key doesn't look like a JWT. Ensure you're using the Supabase Anon public key, not another service's key."
    );
  }
  if (supabaseUrl && !/^https?:\/\/.*\.supabase\.co$/i.test(supabaseUrl)) {
    console.warn(
      "Supabase URL format unexpected. Expected https://<ref>.supabase.co"
    );
  }
  console.info(
    "Supabase config — URL present?",
    !!supabaseUrl,
    "Anon key present?",
    !!supabaseAnonKey
  );
}

// Force native fetch to avoid extension/tooling monkey patches
const nativeFetch: typeof window.fetch = (...args) => window.fetch(...args);

// In demo mode, use a mock Supabase client; otherwise create the real one
// Cast demo client to SupabaseClient so downstream code retains type safety
let _supabase: SupabaseClient;
if (isDemoMode()) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { createDemoClient } = require("../demo/demoSupabase");
  _supabase = createDemoClient() as SupabaseClient;
} else {
  _supabase = createClient(supabaseUrl!, supabaseAnonKey!, {
    global: { fetch: nativeFetch },
    auth: {
      flowType: 'pkce',
      detectSessionInUrl: true,
    },
  });
}

export const supabase: SupabaseClient = _supabase;

// Database types based on current merchant-based schema (lightweight, for reference only)
export interface Database {
  public: {
    Tables: {
      merchants: {
        Row: {
          id: string;
          email: string | null;
          login_token: string | null;
          status: string | null;
          // Note: column name remains user_config_id for backward compatibility
          user_config_id: string | null;
          webhook_url: string | null;
        };
        Insert: {
          id?: string;
          email?: string | null;
          login_token?: string | null;
          status?: string | null;
          user_config_id?: string | null;
          webhook_url?: string | null;
        };
        Update: {
          id?: string;
          email?: string | null;
          login_token?: string | null;
          status?: string | null;
          user_config_id?: string | null;
          webhook_url?: string | null;
        };
      };
      merchant_config: {
        Row: {
          id: string;
          config_object: any | null;
          business_info: any | null;
          created_at: string;
          platform: string | null;
          updated_at: string;
          merchant_id: string | null;
          client_name?: string | null;
        };
        Insert: {
          id?: string;
          config_object?: any | null;
          business_info?: any | null;
          created_at?: string;
          platform?: string | null;
          updated_at?: string;
          merchant_id?: string | null;
          client_name?: string | null;
        };
        Update: {
          id?: string;
          config_object?: any | null;
          business_info?: any | null;
          created_at?: string;
          platform?: string | null;
          updated_at?: string;
          merchant_id?: string | null;
          client_name?: string | null;
        };
      };
      providers: {
        Row: {
          id: string;
          is_active: boolean | null;
          name: string | null;
          provider_type: string | null;
          merchant_id: string | null;
        };
        Insert: {
          id?: string;
          is_active?: boolean | null;
          name?: string | null;
          provider_type?: string | null;
          merchant_id?: string | null;
        };
        Update: {
          id?: string;
          is_active?: boolean | null;
          name?: string | null;
          provider_type?: string | null;
          merchant_id?: string | null;
        };
      };
      merchant_provider_map: {
        Row: {
          merchant_id: string | null;
          provider_id: string | null;
          stock_provider: boolean | null;
          orders_provider: boolean | null;
        };
        Insert: {
          merchant_id?: string | null;
          provider_id?: string | null;
          stock_provider?: boolean | null;
          orders_provider?: boolean | null;
        };
        Update: {
          merchant_id?: string | null;
          provider_id?: string | null;
          stock_provider?: boolean | null;
          orders_provider?: boolean | null;
        };
      };
    };
  };
}

// Type for the config object structure
export interface ShopConfig {
  client_name?: string;
  // Automatic brand/model/gender/category tags (W5). Absent = off.
  auto_tags?: {
    enabled?: boolean;
    brand?: boolean;
    model?: boolean;
    gender?: boolean;
    category?: boolean;
  };
  // Full size runs (W8): new products get every size of the brand's run. Absent = off.
  catalog?: {
    full_size_runs?: boolean;
  };
  platform?: {
    shopify?: {
      shop_domain: string;
      access_token: string;
      location_id?: string;
    };
  };
  processors?: {
    title?: {
      businessRules?: Array<{
        pattern: string;
        replacement: string;
      }>;
    };
    productType?: {
      businessRules?: Array<{
        pattern: string;
        replacement: string;
      }>;
    };
    categories?: {
      categoryRules?: Array<{
        pattern: string;
        category: string;
      }>;
    };
    description?: {
      templates?: Array<{
        category: string;
        template: string;
      }>;
    };
    tags?: {
      taggingRules?: Array<{
        pattern: string;
        tags: string[];
      }>;
    };
    pricing?: {
      pricingTiers?: Array<{
        minPrice: number;
        maxPrice: number;
        markup: number;
      }>;
    };
  };
  translations?: {
    size_name?: string;
    size_mappings?: { [key: string]: string };
    size_value_prefix?: string;
    size_value_suffix?: string;
  };
  business_rules?: {
    /**
     * "Never sync" rules. Plain strings are exact matches (the shape of every live entry);
     * a {value, mode} object carries an explicit match mode. Both are read by the same
     * matcher in the workers, and set_merchant_removed_products preserves either shape.
     */
    skip_list?: Array<string | { value: string; mode: "contains" | "exact" }>;
    allowed_list?: Array<string | { title: string; sku: string }>;
    stock_providers?: string[];
    variant_sku_format?: "base_only" | "base_and_size";
    /**
     * Store-level keyword allow-list: only products matching one of these are UPLOADED,
     * across every supplier. Creation-only by design — applying it to updates would stop
     * non-matching products already on the store from syncing, freezing their stock.
     * Entries are persisted with an explicit per-entry mode.
     */
    creation_allow_list?: Array<{ value: string; mode: "contains" | "exact" }>;
    import_mode?: "in_stock" | "all"; // creation only; absent = in stock only (W7)
    require_photo_for_import?: boolean; // creation only; absent = off. Products no supplier has a photo for are held back (W3)
    update_schema?: {
      product?: {
        title?: boolean;
        brand?: boolean;
        image?: boolean;
        category?: boolean;
        productType?: boolean;
      };
      variant?: {
        price?: boolean;
        stock?: boolean;
      };
      clientConfig?: {
        standardTags?: string[];
        standardDescription?: boolean;
        skuInProductType?: boolean;
        skuInModelDetailsMetafield?: boolean;
        useCategoryTags?: boolean;
        newProductStatus?: "ACTIVE" | "DRAFT";
        customMetafields?: Array<{
          key: string;
          value: string;
        }>;
      };
    };
  };
  pricing?: {
    price_tiers?: Array<{
      min?: number;
      max?: number;
      method?: 'multiply' | 'add';
      multiplier?: number; // used when method === 'multiply'
      margin?: number;     // used when method === 'add'
    }>;
    vat_rate?: number;
    delivery_fee?: number;
    global_discount_percent?: number; // applied after rounding across all prices
    rounding_rules?: {
      round_to?: string | number; // string: "to_1", "to_0.50", "ending_99"; number: legacy support
    };
  };
  branding?: {
    standard_tags?: string[];
  };
  category_rules?: Array<{
    keywords?: string[];
    shopify_category_id?: string;
    product_type?: string;
  }>;
  tag_rules?: Array<{
    condition?: {
      keywords?: string[];
    };
    tags?: string[];
  }>;
  description_template?: string;
  product_features?: string[];
  processor_overrides?: {
    keep_database_processors?: string[];
  };
}
